import React from "react";
import { DialogType } from "@widgets/CommonDialog";
import { ApprovalSubmitUserInfo } from "@shared/api/approvalSubmitApi";
import { fnGetDataSet } from "@shared/api/restApi";
import { showPopupModal } from "@widgets/CommonModal";
import ApproveFormPopup from "@widgets/commonpopup/ApproveFormPopup";

export interface ApprovalSubmitParams {
  menuCode: string;
  noRef: string;
  userInfo: ApprovalSubmitUserInfo;
  submitApproval: (payload: any) => Promise<any>;
  showDialog: (type: DialogType, message: string) => Promise<any>;
  openWindow: (url: string) => Window | null | void;
  noUrlMessage?: string;
  popupBlockedMessage?: string;
  noFormMessage?: string;
}

/**
 * 상신에 사용할 결재 양식(no_form)을 결정한다.
 * 양식이 1건이면 팝업 없이 그대로 사용하고, 2건 이상이면 양식도움창에서 고르게 한다.
 * 사용자가 팝업을 닫거나 취소하면 undefined를 돌려 상신을 중단시킨다.
 */
const resolveNoForm = async (
  menuCode: string,
  userInfo: ApprovalSubmitUserInfo,
  showDialog: (type: DialogType, message: string) => Promise<any>,
  noFormMessage: string
): Promise<string | undefined> => {
  const data: any = await fnGetDataSet({
    id: "sn_popup_form_01",
    params: {
      cd_firm: userInfo.cd_firm,
      cd_menu: menuCode,
    },
  });

  // 조회 실패 시 fnGetDataSet이 이미 오류 다이얼로그를 띄우므로 여기서 다시 알리지 않는다.
  if (!data) return undefined;

  const forms: any[] = data[0] ?? [];
  if (forms.length === 0) {
    await showDialog("alter", noFormMessage);
    return undefined;
  }

  if (forms.length === 1) {
    return forms[0].no_form;
  }

  const result = await showPopupModal({
    title: "양식도움창",
    children: React.createElement(ApproveFormPopup, { dataSource: forms }),
    width: "400px",
    height: "450px",
  });

  // 취소(undefined) / 닫기("close") 는 상신 중단으로 처리한다.
  if (!result || result === "close") return undefined;

  return result[0]?.no_form;
};

export const executeApprovalSubmit = async ({
  menuCode,
  noRef,
  userInfo,
  submitApproval,
  showDialog,
  openWindow,
  noUrlMessage = "결재창 URL을 수신하지 못했습니다.",
  popupBlockedMessage = "팝업이 차단되었습니다. 브라우저 팝업 허용 후 다시 시도해 주세요.",
  noFormMessage = "등록된 전자결재 양식이 없습니다.",
}: ApprovalSubmitParams) => {
  const noForm = await resolveNoForm(
    menuCode,
    userInfo,
    showDialog,
    noFormMessage
  );
  if (!noForm) return;

  const result = await submitApproval({
    menuCode,
    noRef,
    noForm,
    userInfo,
  });
  if (!result?.status) return;

  await openApprovalWindow(
    result,
    showDialog,
    openWindow,
    noUrlMessage,
    popupBlockedMessage
  );
};

export interface ApprovalViewParams {
  menuCode: string;
  noRef: string;
  /** 그리드에 표출된 결재번호(no_approve). 비어 있으면 결재 연동된 건이 아니다. */
  noApprove?: string;
  userInfo: ApprovalSubmitUserInfo;
  viewApproval: (payload: any) => Promise<any>;
  showDialog: (type: DialogType, message: string) => Promise<any>;
  openWindow: (url: string) => Window | null | void;
  notLinkedMessage?: string;
  noUrlMessage?: string;
  popupBlockedMessage?: string;
}

/**
 * 상신된 결재문서를 아마란스 문서보기 팝업으로 다시 연다.
 * 그리드 결재번호(no_approve) 컬럼 더블클릭에서 호출한다.
 */
export const executeApprovalView = async ({
  menuCode,
  noRef,
  noApprove,
  userInfo,
  viewApproval,
  showDialog,
  openWindow,
  notLinkedMessage = "결재 연동된 건이 아닙니다.",
  noUrlMessage = "결재문서 URL을 수신하지 못했습니다.",
  popupBlockedMessage = "팝업이 차단되었습니다. 브라우저 팝업 허용 후 다시 시도해 주세요.",
}: ApprovalViewParams) => {
  if (!noApprove || !noRef) {
    await showDialog("alter", notLinkedMessage);
    return;
  }

  const result = await viewApproval({ menuCode, noRef, userInfo });
  if (!result?.status) return;

  await openApprovalWindow(
    result,
    showDialog,
    openWindow,
    noUrlMessage,
    popupBlockedMessage
  );
};

const openApprovalWindow = async (
  result: any,
  showDialog: (type: DialogType, message: string) => Promise<any>,
  openWindow: (url: string) => Window | null | void,
  noUrlMessage: string,
  popupBlockedMessage: string
) => {
  // callApi 변환 결과(data.fullUrl)와 서버 원본 구조(result.return_item.fullUrl)를 모두 허용한다.
  const fullUrl =
    result?.data?.fullUrl ||
    result?.result?.return_item?.fullUrl ||
    result?.return_item?.fullUrl ||
    result?.fullUrl;

  if (!fullUrl) {
    await showDialog("alter", noUrlMessage);
    return;
  }

  const popup = openWindow(fullUrl);
  if (popup === null) {
    await showDialog("alter", popupBlockedMessage);
  }
};
